import { useEffect } from "react";
import { useAtom } from "jotai";
import { Slider } from "@mui/material";
import { firmSizeRangeAtom, dataConfigAtom } from "../state/atoms";
import { firmSizeFTEOptions, getRangeEdges } from "../constants";

// FirmSizeRangeSlider — generic multi-handle slider over the FirmSizeFTE bin edges.
//
// The underlying bin edges are firmSizeFTEOptions: [0, 5, 10, 20, 50, 100, 250, 500, 1000, 2_000_000].
// The 2_000_000 sentinel represents "1000+". The slider operates on bin indices (0..9) so handles
// always snap cleanly to bin boundaries; values stored in firmSizeRangeAtom are the actual bin edges,
// not indices, so they survive any future bin changes.
//
// Visuals:
//   - Per-segment coloured rail via linear-gradient with hard stops at handle positions.
//   - Small black thumbs (no fill colour interaction with segments).
//   - Vertical legend rendered BELOW the slider: each segment as [colour swatch] [name] [range].
//
// Constraints (per handle position i in an N-handle slider):
//   - handle[i] ∈ [i + 1, MAX_INDEX - (N - i)]
//     Lower bound i+1 reserves bin 0..i for the segments to the left.
//     Upper bound MAX_INDEX-(N-i) reserves enough room on the right for the remaining handles
//     (i+1..N-1) plus a single-bin last segment "1000+".
//   - Strict ordering: handle[i] < handle[i+1] (enforced by sweep, not relying on MUI disableSwap
//     alone — disableSwap fails when MUI sends bunched values from a fast drag).
//   - This guarantees: pushing any handle as far right as possible can never collapse later
//     handles past their valid range; the last segment always remains as at least the "1000+"
//     bucket.
//
// Props:
//   dashboardId   — required, key into firmSizeRangeAtom (e.g. "smallBusinessMapIntensity")
//   numHandles    — 1, 2, or 4 internal split-points
//   segmentLabels — array of (numHandles + 1) names for the legend (e.g. ["Small", "Medium", "Large"])

// Bin edges — initialized from hardcoded defaults, updated from dataConfigAtom on first render.
// Module-level so the helper functions (valueToIndex, indexToValue, constrainHandleIndices) work.
let BIN_EDGES = [...firmSizeFTEOptions];
let MAX_INDEX = BIN_EDGES.length - 1;

// Green → blue palette, 5 stops for max 5 segments. Picked for accessibility and the small-business
// theme (blue end aligns with the category icon colour #1976d2).
const SEGMENT_COLOURS = [
  "#2e7d32", // dark green
  "#66bb6a", // light green
  "#fdd835", // amber (transition)
  "#42a5f5", // light blue
  "#1976d2", // dark blue (matches Small Business category icon)
];

// Pick N colours from the 5-stop palette evenly.
function pickColours(numSegments) {
  if (numSegments <= 1) return [SEGMENT_COLOURS[0]];
  if (numSegments === 2) return [SEGMENT_COLOURS[0], SEGMENT_COLOURS[4]];
  if (numSegments === 3) return [SEGMENT_COLOURS[0], SEGMENT_COLOURS[2], SEGMENT_COLOURS[4]];
  if (numSegments === 4) return [SEGMENT_COLOURS[0], SEGMENT_COLOURS[1], SEGMENT_COLOURS[3], SEGMENT_COLOURS[4]];
  return SEGMENT_COLOURS; // 5
}

function formatEdge(value) {
  if (value >= 1_000_000) return "1000+";
  return String(value);
}

// Format an inclusive segment range like "0–49", "50–99", "1000+"
function formatRange(startEdge, endEdge) {
  if (endEdge >= 1_000_000) return `${startEdge}+`;
  return `${startEdge}–${endEdge - 1}`;
}

function valueToIndex(value) {
  const idx = BIN_EDGES.indexOf(value);
  if (idx !== -1) return idx;
  let best = 0;
  let bestDiff = Infinity;
  BIN_EDGES.forEach((edge, i) => {
    const diff = Math.abs(edge - value);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  });
  return best;
}

function indexToValue(index) {
  return BIN_EDGES[Math.max(0, Math.min(MAX_INDEX, index))];
}

// Pure constraint pipeline — used both by handleChange and on-mount self-heal of stale storage.
// Takes an array of slider indices and returns a valid (clamped, ordered, in-range) array.
// When fullRange=true (1-handle only), the handle can reach both ends for single-category selection.
function constrainHandleIndices(arr, fullRange = false) {
  const out = [...arr];
  const N = out.length;
  const absMin = (i) => (fullRange && N === 1) ? 0 : i + 1;
  const absMax = (i) => (fullRange && N === 1) ? MAX_INDEX : MAX_INDEX - (N - i);
  for (let i = 0; i < N; i++) {
    out[i] = Math.max(absMin(i), Math.min(absMax(i), out[i]));
  }
  for (let i = 1; i < N; i++) {
    if (out[i] <= out[i - 1]) out[i] = out[i - 1] + 1;
    if (out[i] > absMax(i)) out[i] = absMax(i);
  }
  for (let i = N - 2; i >= 0; i--) {
    if (out[i] >= out[i + 1]) out[i] = out[i + 1] - 1;
    if (out[i] < absMin(i)) out[i] = absMin(i);
  }
  return out;
}

function FirmSizeRangeSlider({ dashboardId, numHandles, segmentLabels, segmentColors, allowFullRange, invertible }) {
  const [firmSizeRange, setFirmSizeRange] = useAtom(firmSizeRangeAtom);
  const [dataConfig] = useAtom(dataConfigAtom);
  const slot = firmSizeRange?.[dashboardId];
  const handles = slot?.handles ?? [];
  const inverted = slot?.inverted ?? false;

  // Update bin edges from API config when available
  const configEdges = getRangeEdges(dataConfig, "firmsize_fte", null);
  if (configEdges && configEdges.length > 0 && configEdges.length !== BIN_EDGES.length) {
    BIN_EDGES = [...configEdges];
    MAX_INDEX = BIN_EDGES.length - 1;
  }

  const sliderValue = handles.map(valueToIndex);
  const numSegments = numHandles + 1;
  const baseColours = segmentColors ?? pickColours(numSegments);
  // When inverted, swap colours so the dark colour highlights the now-selected segment
  const colours = (invertible && inverted) ? [...baseColours].reverse() : baseColours;

  const handleInvertToggle = () => {
    setFirmSizeRange({
      ...firmSizeRange,
      [dashboardId]: { ...slot, inverted: !inverted },
    });
  };

  const handleChange = (_event, newValue) => {
    const arr = Array.isArray(newValue) ? [...newValue] : [newValue];
    const constrained = constrainHandleIndices(arr, allowFullRange);
    const newHandles = constrained.map(indexToValue);
    setFirmSizeRange({
      ...firmSizeRange,
      [dashboardId]: { ...slot, handles: newHandles },
    });
  };

  // Self-heal: if stored handles are stale/invalid (e.g. handle count changed from 2→4),
  // reset to constrained defaults and write back to the atom.
  useEffect(() => {
    if (sliderValue.length !== numHandles) {
      // Handle count mismatch — reset with evenly distributed handles
      const step = Math.floor(MAX_INDEX / (numHandles + 1));
      const newIndices = Array.from({ length: numHandles }, (_, i) => Math.min((i + 1) * step, MAX_INDEX - (numHandles - i)));
      const constrained = constrainHandleIndices(newIndices, allowFullRange);
      const newHandles = constrained.map(indexToValue);
      setFirmSizeRange({
        ...firmSizeRange,
        [dashboardId]: { ...slot, handles: newHandles },
      });
      return;
    }
    const normalized = constrainHandleIndices(sliderValue, allowFullRange);
    const changed = normalized.some((v, i) => v !== sliderValue[i]);
    if (changed) {
      const newHandles = normalized.map(indexToValue);
      setFirmSizeRange({
        ...firmSizeRange,
        [dashboardId]: { ...slot, handles: newHandles },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the per-segment rail gradient. Stops are at handle positions as percentages of MAX_INDEX.
  // Hard stops produce solid colour bands rather than smooth blends.
  const railGradient = (() => {
    const stops = [];
    let prevPct = 0;
    sliderValue.forEach((idx, i) => {
      const pct = (idx / MAX_INDEX) * 100;
      stops.push(`${colours[i]} ${prevPct}%`);
      stops.push(`${colours[i]} ${pct}%`);
      stops.push(`${colours[i + 1]} ${pct}%`);
      prevPct = pct;
    });
    stops.push(`${colours[colours.length - 1]} ${prevPct}%`);
    stops.push(`${colours[colours.length - 1]} 100%`);
    return `linear-gradient(to right, ${stops.join(", ")})`;
  })();

  // Build the legend rows: one per segment, with start/end values and colour.
  // Skip zero-width segments (occur when slider is at extreme ends with allowFullRange).
  const segments = [];
  let prevEdgeIndex = 0;
  for (let i = 0; i <= numHandles; i++) {
    const endIndex = i === numHandles ? MAX_INDEX : sliderValue[i];
    if (prevEdgeIndex === endIndex) {
      prevEdgeIndex = endIndex;
      continue;
    }
    const startVal = BIN_EDGES[prevEdgeIndex];
    const endVal = BIN_EDGES[endIndex];
    let segName = segmentLabels?.[i] ?? `Segment ${i + 1}`;
    if (invertible && inverted) {
      segName = segName.replace("(included)", "(__INC__)").replace("(excluded)", "(included)").replace("(__INC__)", "(excluded)");
    }
    segments.push({
      colour: colours[i],
      name: segName,
      range: formatRange(startVal, endVal),
    });
    prevEdgeIndex = endIndex;
  }

  // Marks at every bin edge for snap visibility (no labels — keeps it clean in the narrow filter column)
  const marks = BIN_EDGES.map((_, i) => ({ value: i }));

  if (sliderValue.length !== numHandles) {
    return (
      <div className="text-xs text-red-600 italic">
        FirmSizeRangeSlider: stored handles ({sliderValue.length}) does not match numHandles ({numHandles}) for dashboard "{dashboardId}".
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="px-2 pt-2 pb-1">
        <Slider
          value={numHandles === 1 ? sliderValue[0] : sliderValue}
          onChange={handleChange}
          min={0}
          max={MAX_INDEX}
          step={1}
          marks={marks}
          disableSwap
          valueLabelDisplay="auto"
          valueLabelFormat={(idx) => formatEdge(indexToValue(idx))}
          sx={{
            color: "#000",
            height: 8,
            "& .MuiSlider-rail": {
              opacity: 1,
              background: railGradient,
              border: "1px solid #555",
              height: 8,
            },
            "& .MuiSlider-track": {
              background: "transparent",
              border: "none",
            },
            "& .MuiSlider-thumb": {
              backgroundColor: "#000",
              width: 12,
              height: 12,
              border: "2px solid #fff",
              boxShadow: "0 0 0 1px #000",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0 0 0 6px rgba(0,0,0,0.16)",
              },
              "&.Mui-active": {
                boxShadow: "0 0 0 8px rgba(0,0,0,0.16)",
              },
            },
            "& .MuiSlider-mark": {
              backgroundColor: "#fff",
              width: 2,
              height: 6,
              opacity: 0.6,
            },
            "& .MuiSlider-markActive": {
              opacity: 0.8,
              backgroundColor: "#fff",
            },
          }}
        />
      </div>
      <div className="px-2 mt-2 text-xs">
        <div className="flex items-start">
          <div className="flex-1">
            {segments.map((seg, i) => (
              <div key={i} className="flex items-center mb-1">
                <span
                  className="inline-block w-3 h-3 mr-2 border border-gray-700"
                  style={{ backgroundColor: seg.colour }}
                />
                <span className="font-semibold mr-2">{seg.name}</span>
                <span className="text-gray-600">{seg.range}</span>
              </div>
            ))}
          </div>
          {invertible && (
            <button
              onClick={handleInvertToggle}
              className="ml-2 mt-0.5 px-2 py-1 border rounded text-xs whitespace-nowrap"
              style={{
                borderColor: inverted ? "#424242" : "#ccc",
                backgroundColor: inverted ? "#424242" : "#fff",
                color: inverted ? "#fff" : "#424242",
              }}
              title={inverted ? "Showing Medium-Large Business" : "Showing Small Business"}
            >
              {inverted ? "Invert: On" : "Invert"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FirmSizeRangeSlider;
